import { useId, useState, type FormEvent } from 'react';
import { Banner, Button, Dialog, type ComboboxOption } from '../../../ds/components';
import { errorMessage } from '../../../lib/api';
import { ContactPicker } from '../create/ContactPicker';
import type { MailboxRowData } from '../types';
import styles from '../create/create.module.css';

export interface ContactSearchDialogProps {
  row: MailboxRowData;
  onAssign: (contactId: string) => Promise<void>;
  onIgnore: () => Promise<void>;
  onClose: () => void;
}

/**
 * „Kontakt suchen" of a mail without a match (After-Sales 01, no frame; own texts): pick the contact → the mail is
 * filed on the contact's timeline. „Ignorieren" drops automatic replies and spam from the list.
 */
export function ContactSearchDialog({ row, onAssign, onIgnore, onClose }: ContactSearchDialogProps) {
  const formId = useId();
  const [contact, setContact] = useState<ComboboxOption | null>(null);
  const [pending, setPending] = useState<'assign' | 'ignore' | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function act(kind: 'assign' | 'ignore') {
    setPending(kind);
    setError(null);
    try {
      if (kind === 'assign') {
        if (!contact) return;
        await onAssign(contact.id);
      } else {
        await onIgnore();
      }
      onClose();
    } catch (caught) {
      setError(errorMessage(caught));
    } finally {
      setPending(null);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!contact) {
      setError('Bitte einen Kontakt wählen.');
      return;
    }
    void act('assign');
  }

  return (
    <Dialog
      open
      onClose={onClose}
      width={560}
      initialFocus="none"
      eyebrow="Postfach"
      title="Kontakt suchen"
      subtitle={`${row.senderEmail} · ${row.subject}`}
      footerNote={
        <Button
          variant="ghost"
          size="small"
          loading={pending === 'ignore'}
          onClick={() => void act('ignore')}
        >
          Ignorieren
        </Button>
      }
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={pending !== null}>
            Abbrechen
          </Button>
          <Button type="submit" form={formId} loading={pending === 'assign'} disabled={pending === 'ignore'}>
            Zuordnen
          </Button>
        </>
      }
    >
      <form id={formId} className={styles.form} onSubmit={submit} noValidate>
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <ContactPicker value={contact} onChange={setContact} autoFocus />
        <p className={styles.note}>Die E-Mail erscheint danach im Verlauf des Kontakts.</p>
      </form>
    </Dialog>
  );
}
